/**
 * Génération des QR codes d'appairage.
 *
 * Le QR code imprimé sur l'appareil est le point d'entrée de TOUT le parcours client : l'écran
 * 01A le scanne, en extrait le numéro de série et la clé d'appairage, et c'est ce couple qui
 * autorise la création de l'exploitation. Sa charge utile est donc contractuelle et vit ici,
 * dans une seule fonction.
 *
 * Le rendu se fait dans le navigateur : les secrets d'appairage ne sont connus en clair qu'à
 * l'import (ou après régénération lors d'une désassociation) et ne doivent pas repartir vers un
 * service tiers pour être transformés en image.
 */

import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

import { Bouton } from './Boutons';
import { Modale } from './Modale';

/** Version du format de charge utile du QR code. */
export const VERSION_QR = 1;

/**
 * Charge utile encodée : `GVZ:1|SN=<numéro de série>|K=<clé>`.
 * La clé est omise pour un appareil dont le secret n'est plus connu du back-office — le QR
 * reste alors utilisable pour identifier l'appareil, mais pas pour l'appairer.
 */
export function chargeUtileQr(numeroSerie: string, secret?: string | null): string {
  const base = `GVZ:${VERSION_QR}|SN=${numeroSerie}`;
  return secret ? `${base}|K=${secret}` : base;
}

export interface EtiquetteQr {
  numeroSerie: string;
  /** Secret d'appairage en clair. Absent = QR d'identification seule. */
  secret?: string | null;
  /** Ligne libre : lot, modèle, date de fabrication. */
  complement?: string;
}

/** Image d'un QR code, générée à la volée. */
export function QrCode({
  valeur,
  taille = 296,
  alt,
}: {
  valeur: string;
  taille?: number;
  alt: string;
}): JSX.Element {
  const [source, setSource] = useState<string | null>(null);
  const [echec, setEchec] = useState(false);

  useEffect(() => {
    let actif = true;
    setEchec(false);
    QRCode.toDataURL(valeur, {
      width: taille,
      margin: 1,
      // Niveau M : l'étiquette est collée sur un boîtier posé en plein champ, elle prend la
      // poussière et les rayures. 15 % de redondance est le minimum raisonnable.
      errorCorrectionLevel: 'M',
      color: { dark: '#14181FFF', light: '#FFFFFFFF' },
    })
      .then((url) => {
        if (actif) setSource(url);
      })
      .catch(() => {
        if (actif) setEchec(true);
      });
    return () => {
      actif = false;
    };
  }, [valeur, taille]);

  if (echec) {
    return (
      <span className="gv-corps-s" style={{ color: 'var(--gv-rouge)' }}>
        QR illisible
      </span>
    );
  }

  if (!source) {
    return <span className="gv-squelette" style={{ width: 148, height: 148 }} />;
  }

  return <img src={source} alt={alt} width={148} height={148} />;
}

/** Étiquette imprimable : QR, numéro de série lisible, clé lisible. */
export function Etiquette({ etiquette }: { etiquette: EtiquetteQr }): JSX.Element {
  const valeur = chargeUtileQr(etiquette.numeroSerie, etiquette.secret);
  return (
    <div className="gv-etiquette">
      <QrCode valeur={valeur} alt={`QR code de l’appareil ${etiquette.numeroSerie}`} />
      <span className="gv-etiquette__sn">{etiquette.numeroSerie}</span>
      {etiquette.secret ? (
        <span className="gv-etiquette__cle">clé {etiquette.secret}</span>
      ) : (
        <span className="gv-etiquette__cle">sans clé</span>
      )}
      {etiquette.complement ? (
        <span className="gv-etiquette__cle">{etiquette.complement}</span>
      ) : null}
    </div>
  );
}

/**
 * Planche de QR codes, prête à imprimer.
 *
 * Les secrets d'appairage n'existent en clair qu'ici et maintenant : l'API ne les relit jamais
 * (seule leur empreinte est conservée). La planche prévient l'opérateur — s'il ferme sans
 * imprimer, il faudra régénérer les secrets, ce qui invalide les étiquettes déjà collées.
 */
export function PlancheQr({
  ouverte,
  surFermeture,
  etiquettes,
  avertissementSecret = true,
}: {
  ouverte: boolean;
  surFermeture: () => void;
  etiquettes: readonly EtiquetteQr[];
  avertissementSecret?: boolean;
}): JSX.Element {
  return (
    <Modale
      titre={`Planche de QR codes — ${etiquettes.length} appareil${etiquettes.length > 1 ? 's' : ''}`}
      ouverte={ouverte}
      surFermeture={surFermeture}
      large
      pied={
        <>
          <Bouton variante="discret" onClick={surFermeture}>
            Fermer
          </Bouton>
          <Bouton
            variante="primaire"
            onClick={() => {
              window.print();
            }}
          >
            Imprimer
          </Bouton>
        </>
      }
    >
      {avertissementSecret && etiquettes.some((e) => Boolean(e.secret)) ? (
        <div className="gv-bandeau gv-bandeau--attention gv-sans-impression">
          <span>
            Les clés d’appairage ne sont affichées qu’une fois : le serveur n’en conserve que
            l’empreinte. Imprimez cette planche maintenant — sinon il faudra régénérer les secrets,
            ce qui rend caduques les étiquettes déjà posées.
          </span>
        </div>
      ) : null}

      <div className="gv-planche">
        {etiquettes.map((e) => (
          <Etiquette key={e.numeroSerie} etiquette={e} />
        ))}
      </div>
    </Modale>
  );
}
